import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Phone, Lock, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const PhoneAuthModal = ({ open, onClose, redirectPath = null }) => {
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  
  const { sendOTP, verifyOTP } = useAuth();
  const { toast } = useToast();

  // Initialize reCAPTCHA when modal opens
  useEffect(() => {
    if (open && step === 'phone') {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        const container = document.getElementById('recaptcha-container');
        if (container && !recaptchaReady) {
          console.log('📦 Initializing visible reCAPTCHA...');
          setRecaptchaReady(true);
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [open, step, recaptchaReady]);

  // Countdown timer for resend OTP
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // Add +91 prefix if not present (for India)
    if (digits.length > 0 && !value.startsWith('+')) {
      return `+91${digits}`;
    }
    
    return value;
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    // Validate phone number
    if (!phoneNumber || phoneNumber.length < 13) {
      toast({
        title: 'Invalid Phone Number',
        description: 'Please enter a valid 10-digit mobile number',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      const result = await sendOTP(phoneNumber);
      setConfirmationResult(result);
      setStep('otp');
      setCountdown(60); // 60 seconds countdown
      
      toast({
        title: 'OTP Sent!',
        description: `Verification code sent to ${phoneNumber}`,
      });
    } catch (error) {
      console.error('Send OTP error:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      
      let errorMessage = 'Failed to send OTP. Please try again.';
      
      // Use custom error message if available
      if (error.message) {
        errorMessage = error.message;
      } else if (error.code === 'auth/invalid-phone-number') {
        errorMessage = 'Invalid phone number format';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please try again later.';
      } else if (error.code === 'auth/quota-exceeded') {
        errorMessage = 'SMS quota exceeded. Please contact support.';
      } else if (error.code === 'auth/captcha-check-failed') {
        errorMessage = 'reCAPTCHA verification failed. Please refresh and try again.';
      }
      
      toast({
        title: 'Error Sending OTP',
        description: errorMessage,
        variant: 'destructive',
        duration: 6000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6) {
      toast({
        title: 'Invalid OTP',
        description: 'Please enter a valid 6-digit OTP',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      await verifyOTP(confirmationResult, otp);
      
      toast({
        title: 'Success!',
        description: 'You have been signed in successfully',
      });
      
      onClose();
      
      // Redirect to specified path after successful login
      if (redirectPath) {
        setTimeout(() => {
          window.location.href = redirectPath;
        }, 100);
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      
      let errorMessage = 'Invalid OTP. Please try again.';
      
      if (error.code === 'auth/invalid-verification-code') {
        errorMessage = 'Invalid verification code';
      } else if (error.code === 'auth/code-expired') {
        errorMessage = 'Verification code expired. Please request a new one.';
      }
      
      toast({
        title: 'Verification Failed',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    
    setOtp('');
    setLoading(true);

    try {
      const result = await sendOTP(phoneNumber);
      setConfirmationResult(result);
      setCountdown(60);
      
      toast({
        title: 'OTP Resent!',
        description: `New verification code sent to ${phoneNumber}`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to resend OTP. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('phone');
    setOtp('');
    setConfirmationResult(null);
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 'otp' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="p-0 h-auto hover:bg-transparent"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            <Phone className="w-5 h-5" />
            {step === 'phone' ? 'Sign In with Phone' : 'Enter OTP'}
          </DialogTitle>
        </DialogHeader>

        {/* Hidden invisible reCAPTCHA container */}
        <div id="recaptcha-container" style={{ display: 'none' }}></div>

        {step === 'phone' ? (
          <form onSubmit={handleSendOTP} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Mobile Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+91 9876543210"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  className="pl-10"
                  required
                  disabled={loading}
                />
              </div>
              <p className="text-xs text-gray-500">
                Enter your 10-digit mobile number
              </p>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Sending OTP...
                </>
              ) : (
                'Send OTP'
              )}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="otp">Verification Code</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="otp"
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={handleOtpChange}
                  className="pl-10 text-center text-xl tracking-widest"
                  maxLength={6}
                  required
                  disabled={loading}
                  autoFocus
                />
              </div>
              <p className="text-xs text-gray-500 text-center">
                OTP sent to {phoneNumber}
              </p>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600"
              disabled={loading || otp.length !== 6}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Verifying...
                </>
              ) : (
                'Verify OTP'
              )}
            </Button>

            <div className="text-center">
              {countdown > 0 ? (
                <p className="text-sm text-gray-500">
                  Resend OTP in {countdown}s
                </p>
              ) : (
                <Button
                  type="button"
                  variant="link"
                  onClick={handleResendOTP}
                  disabled={loading}
                  className="text-amber-600 hover:text-amber-700"
                >
                  Resend OTP
                </Button>
              )}
            </div>
          </form>
        )}

        <div className="text-center text-xs text-gray-500 mt-4">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PhoneAuthModal;
